import { Charge } from '../../models/Charge'
import { Transaction } from '../../models/Transaction'
import { currencyFormat } from '../../utils/numbers'
import Button from '../app/design/Button'
import Card from '../app/design/Card'
import Divider from '../app/design/Divider'
import Heading from '../app/design/Heading'
import Paragraph from '../app/design/Paragraph'
import { RecordStatusChecklist } from '../common/RecordStatusChecklist'
import { BaseRecordView } from '../common/sections'
import { ContactAvatar } from '../contacts/ContactAvatar'
import { WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    charge: Charge
}

export const ChargeView = ({ charge }: Props) => {
    return (
        <BaseRecordView.Container>
            <BaseRecordView.LeftColumn>
                <Card size="sm">
                    <div className="flex flex-col items-center justify-center gap-y-1">
                        <Heading size="lg" className="text-center">
                            {currencyFormat(charge.chargeAmount ?? 0)}
                        </Heading>

                        {charge.paymeth && Transaction.payMethodStrings().get(charge.paymeth) && (
                            <Paragraph size="lg" className="font-normal text-gray-600">
                                {Transaction.payMethodStrings().get(charge.paymeth)}
                            </Paragraph>
                        )}
                    </div>

                    {charge.client?.id && (
                        <>
                            <Divider className="my-6" />

                            <div className="flex flex-col items-stretch justify-start gap-y-6">
                                {!!charge.subaccount && (
                                    <BaseRecordView.IconField
                                        icon={
                                            <ContactAvatar
                                                user={charge.subaccount}
                                                containerClassName="row-span-2"
                                                hasHoverEffect
                                            />
                                        }
                                        title={charge.subaccount.name ?? ''}
                                        subtitle={charge.subaccount.labelString}
                                        href={`/subaccounts/${charge.subaccount.id}`}
                                    />
                                )}

                                <BaseRecordView.IconField
                                    icon={<ContactAvatar user={charge.client} hasHoverEffect />}
                                    title={charge.client.name ?? ''}
                                    subtitle="Client"
                                    secondaryView={
                                        <a href={`/messages/${charge.client.id}`}>
                                            <Button intent="secondary" size="small">
                                                Chat
                                            </Button>
                                        </a>
                                    }
                                    href={`/clients/${charge.client.id}`}
                                />
                            </div>
                        </>
                    )}
                </Card>

                <Card size="sm" title="Status">
                    <RecordStatusChecklist
                        items={charge.statuses.map((status) => ({
                            title: status.title,
                            subtitle: status.date?.utc().format('MMM Do, YYYY [at] h:mm A'),

                            status: status.date !== undefined ? 'checked' : 'empty',
                        }))}
                        className="mt-6"
                    />
                </Card>
            </BaseRecordView.LeftColumn>

            <BaseRecordView.RightColumn>
                <Card title="Payment details">
                    <div className="mt-8">
                        <WorkflowPage.ReceiptView
                            receipt={{
                                modifiers: {
                                    subtotal: {
                                        amount: charge.total ?? 0,
                                    },
                                    discount: charge.discount ? {} : undefined,
                                    gratuity: charge.gratuity
                                        ? {
                                              amount: charge.gratuityAmount,
                                              percent:
                                                  charge.gratuityType === 'percent'
                                                      ? charge.gratuity
                                                      : undefined,
                                          }
                                        : undefined,
                                    salesTax:
                                        charge.tax !== undefined && (charge.taxAmount ?? 0) > 0
                                            ? {
                                                  amount: charge.taxAmount,
                                                  parenthetical: `${charge.tax}%`,
                                              }
                                            : undefined,
                                },
                                total: charge.chargeAmount ?? 0,
                                totalText: 'Total',
                            }}
                            transaction={charge}
                            onUpdateTransaction={undefined}
                        />
                    </div>
                </Card>

                {charge.memo && (
                    <Card title="Memo">
                        <Paragraph className="mt-8 whitespace-pre-wrap text-gray-600">
                            {charge.memo}
                        </Paragraph>
                    </Card>
                )}
            </BaseRecordView.RightColumn>
        </BaseRecordView.Container>
    )
}
